import { useEffect, useId, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FeldHilfe } from '@/components/FeldHilfe'
import {
  useProduktbildUpload, useBildUebernehmen, useBildVerwendung, useSteckbrief, useSteckbriefSpeichern,
  type BildMeta, type BildQuelle, type BildRolle, type BildArchivAntwort, type BildVerwendung,
  type Produktbild, type SteckbriefFeld, type SteckbriefPatch,
} from '@/api/produktbilder'
import { fetchProductKnowledgeImage, type CanonicalImage } from '@/api/productKnowledge'
import { FELDHILFE, STECKBRIEF_FELDER } from '../feldhilfe'

export const ROLLEN: Record<BildRolle, string> = {
  haupt: 'Hauptbild', neben: 'Nebenbild', detail: 'Detailbild', masse: 'Maße', anwendung: 'Anwendung', verpackung: 'Verpackung',
}
const QUELLEN: Record<BildQuelle, string> = { manuell: 'manuell', shopify: 'Shopify', amazon: 'Amazon', ebay: 'eBay', metro: 'Metro', ki: 'KI' }
const PORTALE: Record<string, string> = { amazon: 'Amazon', ebay: 'eBay', shopify: 'Shopify', metro: 'Metro', kaufland: 'Kaufland' }
const inputClass = 'w-full min-w-0 rounded-md border border-line-2 bg-panel-2 px-3 py-2 text-sm text-ink focus-visible:outline-2 focus-visible:outline-primary'

function seoVorschlag(beschreibung: string, artikelname: string): string {
  const value = `${beschreibung} ${artikelname}`.toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80).replace(/-+$/g, '')
  return value.length >= 3 ? value : 'produktbild'
}
function werteAusMeta(meta: BildMeta): Record<SteckbriefFeld, string> {
  return Object.fromEntries(STECKBRIEF_FELDER.map(feld => [feld, meta[feld]?.value ?? ''])) as Record<SteckbriefFeld, string>
}
export function BildVorschau({ src, alt, className = '' }: { src: string; alt: string; className?: string }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  return failedUrl === src || !src
    ? <p className={`grid place-items-center p-3 text-xs text-coral ${className}`}>Bildvorschau konnte nicht geladen werden.</p>
    : <img src={src} alt={alt} className={`object-contain ${className}`} onError={() => setFailedUrl(src)} />
}

interface Props {
  sku: string; artikelname: string; file?: File; bild?: Produktbild; archiv?: CanonicalImage; onClose: () => void
}
export function BildDialog({ sku, artikelname, file, bild, archiv, onClose }: Props) {
  const steckbrief = useSteckbrief(bild?.file_hash ?? archiv?.fileHash)
  const [preview, setPreview] = useState('')
  const content = useRef<HTMLDivElement>(null)
  useEffect(() => {
    let active = true
    let url: string | undefined
    if (file) {
      url = URL.createObjectURL(file)
      setPreview(url)
    } else if (archiv) {
      void fetchProductKnowledgeImage(archiv.fileHash).then(blob => {
        if (!active) return
        url = URL.createObjectURL(blob)
        setPreview(url)
      }).catch(() => { if (active) setPreview('') })
    }
    return () => { active = false; if (url) URL.revokeObjectURL(url) }
  }, [file, archiv])
  const [uploaded, setUploaded] = useState<BildArchivAntwort | null>(null)
  const [saving, setSaving] = useState(false)
  return (
    <Dialog.Root open onOpenChange={open => { if (!open && !saving) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/50" />
        <Dialog.Content ref={content} tabIndex={-1}
          onEscapeKeyDown={event => { if (saving) event.preventDefault() }}
          onInteractOutside={event => { if (saving) event.preventDefault() }}
          onOpenAutoFocus={event => { event.preventDefault(); content.current?.focus() }}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-lg border border-line-2 bg-panel p-4 text-ink shadow-lg outline-none sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Dialog.Title className="font-display text-lg font-semibold">{bild || uploaded ? 'Bild bearbeiten' : archiv ? 'Bild übernehmen' : 'Bild hochladen'}</Dialog.Title>
              <Dialog.Description className="mt-1 break-words text-xs text-ink-2">{artikelname} · {sku}</Dialog.Description>
            </div>
            <Dialog.Close asChild><Button type="button" variant="ghost" size="icon" aria-label="Fenster schließen" disabled={saving}><X aria-hidden /></Button></Dialog.Close>
          </div>
          <BildVorschau src={uploaded?.public_url ?? bild?.public_url ?? preview} alt="Bildvorschau" className="mt-4 h-40 w-full rounded-md bg-panel-2 sm:h-52" />
          {(bild?.file_hash || archiv?.fileHash) && steckbrief.isPending ? <p className="py-4 text-sm text-ink-2">Steckbrief wird geladen…</p>
            : steckbrief.error ? <div className="space-y-3 py-4"><p role="alert" className="text-sm text-coral">{steckbrief.error.message}</p><Button onClick={() => void steckbrief.refetch()}>Steckbrief erneut laden</Button><Button disabled>Speichern</Button></div>
              : <BildForm sku={sku} artikelname={artikelname} file={file} bild={bild} archiv={archiv}
                meta={steckbrief.data?.meta ?? {}} seoName={bild?.seo_name ?? steckbrief.data?.seo_name ?? archiv?.seoName ?? ''}
                uploaded={uploaded} onUploaded={setUploaded} onBusyChange={setSaving} onClose={onClose} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function BildForm({ sku, artikelname, file, bild, archiv, meta, seoName, uploaded, onUploaded, onBusyChange, onClose }: Props & {
  meta: BildMeta; seoName: string; uploaded: BildArchivAntwort | null; onUploaded: (value: BildArchivAntwort) => void
  onBusyChange: (value: boolean) => void
}) {
  const id = useId()
  const [role, setRole] = useState<BildRolle | ''>(bild?.role ?? (bild?.is_primary ? 'haupt' : ''))
  const [portals, setPortals] = useState<string[]>(bild?.portals ?? (bild?.portal ? [bild.portal] : []))
  const [variant, setVariant] = useState(bild?.variant_sku ?? '')
  const [values, setValues] = useState(() => werteAusMeta(meta))
  const [baseline, setBaseline] = useState(() => werteAusMeta(meta))
  const [usageBaseline, setUsageBaseline] = useState({ role, portals, variant })
  const [seo, setSeo] = useState(seoName)
  const [seoEdited, setSeoEdited] = useState(false)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [patchFailed, setPatchFailed] = useState(false)
  const lock = useRef(false)
  const upload = useProduktbildUpload(sku)
  const uebernehmen = useBildUebernehmen(sku)
  const verwendung = useBildVerwendung(sku)
  const steckbrief = useSteckbriefSpeichern(uploaded?.file_hash ?? bild?.file_hash ?? archiv?.fileHash)
  const editing = !!bild || !!uploaded
  const canDescribe = !bild || !!bild.file_hash
  const proposedSeo = seoEdited || editing || archiv ? seo : seoVorschlag(values.beschreibung, artikelname)
  const allPortals = [...new Set([...Object.keys(PORTALE), ...portals])]

  async function speichern() {
    if (!role || lock.current) return
    lock.current = true
    setSaving(true)
    onBusyChange(true)
    setError('')
    let savedUpload = uploaded
    try {
      if (!editing && (file || archiv)) {
        savedUpload = archiv ? await uebernehmen.mutateAsync({ file_hash: archiv.fileHash, role,
          ...(portals.length ? { portals } : {}), ...(variant.trim() ? { variant_sku: variant.trim() } : {}),
        }) : await upload.mutateAsync({ file: file!, role, portals, variant_sku: variant, seo_name: proposedSeo, beschreibung: values.beschreibung })
        onUploaded(savedUpload)
        // Ein bereits archivierter Hash behält gegebenenfalls seinen vorhandenen Dateinamen.
        const dateiname = savedUpload.public_url.split('/').pop()?.split(/[?#]/)[0]
        setSeo(dateiname?.replace(/\.(jpe?g|png|webp)$/i, '') || proposedSeo)
        setUsageBaseline({ role, portals: [...portals], variant })
        // Beschreibung wurde bereits mit dem Upload gespeichert.
        if (!archiv) setBaseline(previous => ({ ...previous, beschreibung: values.beschreibung }))
      }
      const usage: BildVerwendung = {}
      if (editing) {
        if (role !== usageBaseline.role) usage.role = role
        if (JSON.stringify(portals) !== JSON.stringify(usageBaseline.portals)) usage.portals = portals.length ? portals : null
        if (variant !== usageBaseline.variant) usage.variant_sku = variant || null
        if (Object.keys(usage).length) {
          await verwendung.mutateAsync({ id: savedUpload?.id ?? bild!.id, payload: usage })
          setUsageBaseline({ role, portals: [...portals], variant })
        }
      }
      const felder: SteckbriefPatch = {}
      if (canDescribe) for (const feld of STECKBRIEF_FELDER) {
        if (!editing && !archiv && feld === 'beschreibung') continue
        if (values[feld] !== baseline[feld]) felder[feld] = values[feld].trim() ? values[feld] : null
      }
      if (Object.keys(felder).length) {
        if (!savedUpload?.file_hash && !bild?.file_hash && !archiv?.fileHash) throw new Error('Bildhash fehlt in der Upload-Antwort; Steckbrief kann nicht gespeichert werden')
        await steckbrief.mutateAsync({ felder, zielHash: savedUpload?.file_hash ?? bild?.file_hash ?? archiv?.fileHash })
        setBaseline({ ...values })
      }
      setPatchFailed(false)
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Speichern fehlgeschlagen'
      setPatchFailed(!!savedUpload)
      setError(savedUpload ? `Bild gespeichert, Steckbrief nicht: ${message}` : message)
    } finally {
      lock.current = false
      setSaving(false)
      onBusyChange(false)
    }
  }
  return (
    <form className="mt-4 min-w-0 space-y-5" onSubmit={event => { event.preventDefault(); void speichern() }}>
      <fieldset disabled={saving} className="min-w-0 space-y-3">
        <legend className="flex items-center gap-1 font-display font-semibold">Verwendung <FeldHilfe feld="verwendung" /></legend>
        <div className="grid min-w-0 gap-3 sm:grid-cols-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1"><label htmlFor={`${id}-rolle`} className="text-xs text-ink-2">Rolle</label><FeldHilfe feld="rolle" /></div>
            <select id={`${id}-rolle`} value={role} required className={inputClass} disabled={patchFailed}
              onChange={event => setRole(event.target.value as BildRolle | '')}>
              <option value="">Rolle wählen</option>
              {Object.entries(ROLLEN).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1"><label htmlFor={`${id}-variant`} className="text-xs text-ink-2">Varianten-SKU</label><FeldHilfe feld="variant_sku" /></div>
            <input id={`${id}-variant`} className={`${inputClass} font-mono`} maxLength={100} value={variant} disabled={patchFailed} onChange={event => setVariant(event.target.value)} />
          </div>
        </div>
        <fieldset disabled={patchFailed}>
          <legend className="flex items-center gap-1 text-xs text-ink-2">Portale <FeldHilfe feld="portale" /></legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {allPortals.map(portal => <label key={portal} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={portals.includes(portal)} className="accent-primary focus-visible:outline-2 focus-visible:outline-primary"
                onChange={event => setPortals(previous => event.target.checked ? [...previous, portal] : previous.filter(value => value !== portal))} />
              {PORTALE[portal] ?? portal}
            </label>)}
          </div>
          <p className="mt-2 text-xs text-ink-2">Keine Auswahl: alle Portale.</p>
        </fieldset>
      </fieldset>
      <fieldset disabled={saving || !canDescribe} className="min-w-0 border-t border-line-2 pt-3">
        <legend className="flex items-center gap-1 font-display font-semibold">Steckbrief <FeldHilfe feld="steckbrief" /></legend>
        {!canDescribe && <p className="mb-3 text-xs text-ink-2">Für dieses URL-Bild ist kein Steckbrief im Archiv hinterlegt. Die Verwendung kann bearbeitet werden.</p>}
        <div className="grid min-w-0 gap-3 sm:grid-cols-2">
          {STECKBRIEF_FELDER.map(feld => <div key={feld} data-steckbrief-feld={feld} className={`min-w-0 ${feld === 'beschreibung' || feld === 'notiz_generierung' ? 'sm:col-span-2' : ''}`}>
            <div className="flex flex-wrap items-center gap-1">
              <label htmlFor={`${id}-${feld}`} className="text-xs text-ink-2">{FELDHILFE[feld].titel}</label><FeldHilfe feld={feld} />
              {meta[feld]?.source && <span className="rounded border border-line-2 bg-panel-2 px-1.5 py-0.5 text-[10px] text-ink-2">{QUELLEN[meta[feld]!.source]}</span>}
            </div>
            <textarea id={`${id}-${feld}`} rows={feld === 'beschreibung' || feld === 'notiz_generierung' ? 2 : 1}
              className={`${inputClass} resize-y`} maxLength={feld === 'notiz_generierung' ? 5000 : 2000} value={values[feld]}
              onChange={event => setValues(previous => ({ ...previous, [feld]: event.target.value }))} />
          </div>)}
        </div>
      </fieldset>
      <div className="min-w-0">
        <div className="flex items-center gap-1"><label htmlFor={`${id}-seo`} className="text-xs text-ink-2">SEO-Name</label><FeldHilfe feld="seo_name" /></div>
        <input id={`${id}-seo`} className={`${inputClass} font-mono`} readOnly={editing || !!archiv} disabled={saving} value={proposedSeo}
          required={!editing && !archiv} pattern={'[a-z0-9\\-]{3,80}'} minLength={3} maxLength={80}
          onChange={event => { setSeoEdited(true); setSeo(event.target.value) }} />
      </div>
      {error && <p role="alert" className="break-words text-sm text-coral">{error}</p>}
      <div className="flex flex-wrap justify-end gap-2 border-t border-line-2 pt-4">
        <Button type="button" variant="outline" disabled={saving} onClick={onClose}>Schließen</Button>
        <Button type="submit" disabled={!role || saving}>{saving ? 'Wird gespeichert…' : 'Speichern'}</Button>
      </div>
    </form>
  )
}
